import { useState, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { ChevronDown, Download, Upload, RefreshCw, Loader2, AlertCircle, CheckCircle2, ChevronsUpDown, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { autoMapBulkActionsHeaders } from "@shared/bulk-actions-csv-mapping";

interface FieldConfig {
  key: string;
  label: string;
  required?: boolean;
}

interface EnrichmentData {
  regions?: Array<{ id: string; name: string }>;
  talent?: Array<{ id: string; name: string }>;
}

interface BulkActionsDropdownProps {
  entityType: "leads" | "clients" | "invoices";
  data?: any[];
  dataEndpoint?: string; // Fetched lazily on export instead of upfront
  fields: FieldConfig[];
  onImportComplete?: () => void;
  size?: "default" | "sm" | "lg" | "icon";
  enrichmentData?: EnrichmentData;
}

// Helper to get entity-specific ID label
function getEntityIdLabel(entityType: "leads" | "clients" | "invoices"): string {
  switch (entityType) {
    case "clients": return "Client ID";
    case "leads": return "Lead ID";
    case "invoices": return "Invoice ID";
    default: return "Record ID";
  }
}

export function BulkActionsDropdown({ 
  entityType, 
  data: dataProp = [],
  dataEndpoint,
  fields,
  onImportComplete,
  size = "default",
  enrichmentData
}: BulkActionsDropdownProps) {
  const { toast } = useToast();
  const entityIdLabel = getEntityIdLabel(entityType);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false);
  const [csvData, setCsvData] = useState<string[][]>([]);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [fieldMapping, setFieldMapping] = useState<Record<string, string>>({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [importMode, setImportMode] = useState<"import" | "update">("import");
  const [importResults, setImportResults] = useState<{ 
    success: number; 
    failed: number; 
    skipped?: number; 
    linked?: number; 
    errors: Array<string | { row: number; accountName: string; error: string }>;
    errorSummary?: string[];
  } | null>(null);
  
  // Duplicate detection state
  interface DuplicateRecord {
    rowIndex: number;
    importName: string;
    importData: any;
    existingClients: Array<{ id: string; accountName: string; city?: string; state?: string; matchedFields?: string[]; createdBy?: string }>;
  }
  const [duplicates, setDuplicates] = useState<DuplicateRecord[]>([]);
  const [uniqueRecords, setUniqueRecords] = useState<Array<{ rowIndex: number; data: any }>>([]);
  const [duplicateResolutions, setDuplicateResolutions] = useState<Record<number, { action: "create" | "link" | "skip" | "merge"; linkToClientId?: string; mergeToLeadId?: string }>>({});

  // Pre-import defaults (leads only): apply Client Type / Region / Owner to every imported row
  const [defaultClientType, setDefaultClientType] = useState<string>("");
  const [defaultRegionId, setDefaultRegionId] = useState<string>("");
  const [defaultOwnerId, setDefaultOwnerId] = useState<string>("");
  const [regionPickerOpen, setRegionPickerOpen] = useState(false);
  const [regionQuery, setRegionQuery] = useState("");
  const [creatingRegion, setCreatingRegion] = useState(false);
  const [newRegionName, setNewRegionName] = useState("");

  // Both leads and clients support pre-import defaults (Client Type / Region / Owner)
  const showLeadDefaults = entityType === "leads" || entityType === "clients";

  const { data: clientTypes = [] } = useQuery<Array<{ id: string; name: string }>>({
    queryKey: ["/api/client-types"],
    enabled: showLeadDefaults && (isImportDialogOpen || isProcessing),
  });

  const { data: regions = [] } = useQuery<Array<{ id: string; name: string }>>({
    queryKey: ["/api/regions"],
    enabled: showLeadDefaults && (isImportDialogOpen || isProcessing),
  });

  const { data: usersList = [] } = useQuery<Array<{ id: string; firstName?: string | null; lastName?: string | null; email?: string | null }>>({
    queryKey: ["/api/users"],
    enabled: showLeadDefaults && (isImportDialogOpen || isProcessing),
  });

  const createRegionMutation = useMutation({
    mutationFn: async (name: string) => {
      const res = await apiRequest("POST", "/api/regions", { name });
      return (res as Response).json();
    },
    onSuccess: (region: { id: string; name: string }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/regions"] });
      setDefaultRegionId(region.id);
      setCreatingRegion(false);
      setNewRegionName("");
      setRegionQuery("");
      toast({ title: "Region created", description: region.name });
    },
    onError: (err: any) => {
      toast({ title: "Failed to create region", description: err?.message, variant: "destructive" });
    },
  });

  const formatUser = (u: { firstName?: string | null; lastName?: string | null; email?: string | null; id: string }) =>
    u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.firstName || u.lastName || u.email || u.id;

  const exportToCsv = async () => {
    // Lazily fetch data if not provided (Fix C — avoid upfront loads)
    let data = dataProp;
    if (data.length === 0 && dataEndpoint) {
      try {
        const res = await fetch(dataEndpoint, { credentials: "include" });
        if (!res.ok) throw new Error("Failed to fetch");
        data = await res.json();
      } catch {
        toast({ title: "Export Failed", description: "Could not load data for export.", variant: "destructive" });
        return;
      }
    }

    if (data.length === 0) {
      toast({
        title: "No Data",
        description: "There is no data to export.",
        variant: "destructive",
      });
      return;
    }

    // Build lookup maps for enrichment
    const regionMap = new Map<string, string>();
    const talentMap = new Map<string, string>();
    if (enrichmentData?.regions) {
      enrichmentData.regions.forEach(r => regionMap.set(r.id, r.name));
    }
    if (enrichmentData?.talent) {
      enrichmentData.talent.forEach(e => talentMap.set(e.id, e.name));
    }

    // Build headers - always include recordId as first column (using entity-specific label), then artist for multi-artist support
    const exportFields = ["recordId", "artist", ...fields.map(f => f.key).filter(k => k !== "recordId" && k !== "artist" && k !== "id")];
    const headerLabels = [entityIdLabel, "Artist", ...fields.filter(f => f.key !== "recordId" && f.key !== "artist" && f.key !== "id").map(f => f.label)];

    // Build CSV content
    const csvRows = [headerLabels.join(",")];
    
    data.forEach(item => {
      const row = exportFields.map(field => {
        let value: any;
        
        // Handle special resolved fields
        if (field === "regionName" && item.regionId) {
          value = regionMap.get(item.regionId) || "";
        } else if (field === "talentName" && item.talentId) {
          value = talentMap.get(item.talentId) || "";
        } else {
          value = item[field];
        }
        
        // Handle null/undefined
        if (value === null || value === undefined) {
          return "";
        }
        
        // Handle dates
        if (value instanceof Date) {
          value = value.toISOString();
        } else if (typeof value === "object" && value !== null) {
          value = JSON.stringify(value);
        }
        
        // Escape quotes and wrap in quotes if contains comma or quote
        const stringValue = String(value);
        if (stringValue.includes(",") || stringValue.includes('"') || stringValue.includes("\n")) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      });
      csvRows.push(row.join(","));
    });

    // Download file
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `${entityType}_export_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({
      title: "Export Complete",
      description: `Exported ${data.length} ${entityType} to CSV.`,
    });
  };

  const handleFileSelect = (mode: "import" | "update") => {
    setImportMode(mode);
    setImportResults(null);
    fileInputRef.current?.click();
  };

  const parseCSV = (text: string): string[][] => {
    const rows: string[][] = [];
    let currentRow: string[] = [];
    let currentCell = "";
    let insideQuotes = false;

    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const nextChar = text[i + 1];

      if (insideQuotes) {
        if (char === '"' && nextChar === '"') {
          currentCell += '"';
          i++;
        } else if (char === '"') {
          insideQuotes = false;
        } else {
          currentCell += char;
        }
      } else {
        if (char === '"') {
          insideQuotes = true;
        } else if (char === ",") {
          currentRow.push(currentCell.trim());
          currentCell = "";
        } else if (char === "\n" || (char === "\r" && nextChar === "\n")) {
          currentRow.push(currentCell.trim());
          if (currentRow.length > 0 && currentRow.some(c => c !== "")) {
            rows.push(currentRow);
          }
          currentRow = [];
          currentCell = "";
          if (char === "\r") i++;
        } else if (char !== "\r") {
          currentCell += char;
        }
      }
    }

    // Handle last row
    if (currentCell || currentRow.length > 0) {
      currentRow.push(currentCell.trim());
      if (currentRow.some(c => c !== "")) {
        rows.push(currentRow);
      }
    }

    return rows;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = (event.target?.result as string).replace(/^\uFEFF/, ""); // strip BOM
      const rows = parseCSV(text);
      
      if (rows.length < 2) {
        toast({
          title: "Invalid CSV",
          description: "CSV must have a header row and at least one data row.",
          variant: "destructive",
        });
        return;
      }

      const headers = rows[0];
      const dataRows = rows.slice(1);

      setCsvHeaders(headers);
      setCsvData(dataRows);

      // Auto-map fields based on header names. The alias table + matching
      // logic lives in `shared/bulk-actions-csv-mapping.ts` so it can be
      // unit-tested independently of this component (see
      // `bulk-actions-csv-mapping.test.ts`). `entityType` is forwarded so
      // Clients/Invoices reroute lead-flavoured aliases (e.g.
      // `Account Name` → `accountName`, and for invoices `Amount` →
      // `amount`) to the correct target field keys.
      const autoMapping = autoMapBulkActionsHeaders(headers, fields, entityType);

      setFieldMapping(autoMapping);

      if (importMode === "import") {
        setIsImportDialogOpen(true);
      } else {
        setIsUpdateDialogOpen(true);
      }
    };
    reader.readAsText(file);
    
    // Reset file input
    e.target.value = "";
  };

  const handleMappingChange = (csvIndex: string, fieldKey: string) => {
    setFieldMapping(prev => {
      const newMapping = { ...prev };
      if (fieldKey === "__skip__") {
        delete newMapping[csvIndex];
      } else {
        newMapping[csvIndex] = fieldKey;
      }
      return newMapping;
    });
  };

  const getMappedData = (): Record<string, any>[] => {
    return csvData.map(row => {
      const mappedRow: Record<string, any> = {};
      Object.entries(fieldMapping).forEach(([csvIndex, fieldKey]) => {
        const value = row[parseInt(csvIndex)];
        if (value !== undefined && value !== "") {
          mappedRow[fieldKey] = value;
        }
      });
      return mappedRow;
    });
  };

  const handleImport = async () => {
    setIsProcessing(true);
    setImportResults(null);
    
    try {
      const mappedData = getMappedData();
      
      // For clients and leads, check for duplicates first
      if (entityType === "clients" || entityType === "leads") {
        const checkUrl = entityType === "clients"
          ? "/api/bulk/clients/check-duplicates"
          : "/api/bulk/leads/check-duplicates";
        const checkResponse = await apiRequest("POST", checkUrl, { records: mappedData });
        const checkResult = await (checkResponse as Response).json() as { 
          duplicates: DuplicateRecord[]; 
          uniqueRecords: Array<{ rowIndex: number; data: any }>; 
          totalRecords: number 
        };
        
        if (checkResult.duplicates.length > 0) {
          // Store the results and show duplicate resolution dialog
          setDuplicates(checkResult.duplicates);
          setUniqueRecords(checkResult.uniqueRecords);
          // Initialize resolutions to "skip" by default
          const initialResolutions: Record<number, { action: "create" | "link" | "skip"; linkToClientId?: string }> = {};
          checkResult.duplicates.forEach(dup => {
            initialResolutions[dup.rowIndex] = { action: "skip" };
          });
          setDuplicateResolutions(initialResolutions);
          setIsImportDialogOpen(false);
          setIsDuplicateDialogOpen(true);
          setIsProcessing(false);
          return;
        }
      }
      
      // No duplicates or not applicable - proceed with normal import
      await executeImport(mappedData, []);
    } catch (error: any) {
      toast({
        title: "Import Failed",
        description: error.message || "An error occurred during import.",
        variant: "destructive",
      });
      setIsProcessing(false);
    }
  };
  
  const executeImport = async (mappedData: Record<string, any>[], resolutions: Array<{ rowIndex: number; action: string; linkToClientId?: string }>) => {
    try {
      // Build defaults payload (leads only) — sent as fallbacks for any row missing the field
      const defaults = showLeadDefaults
        ? {
            clientType: defaultClientType || undefined,
            regionId: defaultRegionId || undefined,
            ownerId: defaultOwnerId || undefined,
          }
        : undefined;

      // Use admin-only bulk endpoint
      const response = await apiRequest("POST", `/api/bulk/${entityType}`, {
        records: mappedData,
        duplicateResolutions: resolutions,
        defaults,
      });
      const results = await (response as Response).json() as { success: number; failed: number; skipped?: number; linked?: number; merged?: number; regionsCreated?: number; errors: string[] };
      
      setImportResults(results);
      
      if (results.success > 0 || (results.merged && results.merged > 0)) {
        queryClient.invalidateQueries({ queryKey: [`/api/${entityType}`] });
        onImportComplete?.();
      }
      
      // Refresh regions list if new ones were created
      if (results.regionsCreated && results.regionsCreated > 0) {
        queryClient.invalidateQueries({ queryKey: ["/api/regions"] });
      }

      const parts = [`Successfully imported ${results.success} records.`];
      if (results.regionsCreated && results.regionsCreated > 0) parts.push(`${results.regionsCreated} new regions created.`);
      if (results.merged && results.merged > 0) parts.push(`${results.merged} merged into existing.`);
      if (results.skipped && results.skipped > 0) parts.push(`${results.skipped} skipped.`);
      if (results.linked && results.linked > 0) parts.push(`${results.linked} linked to existing clients.`);
      if (results.failed > 0) parts.push(`${results.failed} failed.`);
      
      toast({
        title: "Import Complete",
        description: parts.join(" "),
        variant: results.failed > 0 ? "destructive" : "default",
      });
    } catch (error: any) {
      toast({
        title: "Import Failed",
        description: error.message || "An error occurred during import.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };
  
  const handleDuplicateResolution = (rowIndex: number, action: "create" | "link" | "skip" | "merge", linkToClientId?: string, mergeToLeadId?: string) => {
    setDuplicateResolutions(prev => ({
      ...prev,
      [rowIndex]: { action, linkToClientId, mergeToLeadId }
    }));
  };
  
  const handleConfirmDuplicateResolution = async () => {
    setIsProcessing(true);
    
    const mappedData = getMappedData();
    const resolutions = Object.entries(duplicateResolutions).map(([rowIndex, resolution]) => ({
      rowIndex: parseInt(rowIndex),
      action: resolution.action === "merge" && resolution.mergeToLeadId
        ? `merge:${resolution.mergeToLeadId}`
        : resolution.action,
      linkToClientId: resolution.linkToClientId
    }));
    
    await executeImport(mappedData, resolutions);
    setIsDuplicateDialogOpen(false);
  };

  const handleUpdate = async () => {
    setIsProcessing(true);
    setImportResults(null);
    
    // Check if id mapping exists
    const hasIdMapping = Object.values(fieldMapping).includes("id");
    if (!hasIdMapping) {
      toast({
        title: `Missing ${entityIdLabel}`,
        description: `You must map a column to '${entityIdLabel}' for updates.`,
        variant: "destructive",
      });
      setIsProcessing(false);
      return;
    }

    try {
      const mappedData = getMappedData();
      console.log("[BulkUpdate] fieldMapping:", fieldMapping);
      console.log("[BulkUpdate] First mapped record:", mappedData[0]);
      console.log("[BulkUpdate] rep field in first record:", mappedData[0]?.rep);
      
      // Use admin-only bulk endpoint
      const response = await apiRequest("PATCH", `/api/bulk/${entityType}`, { records: mappedData });
      const results = await (response as Response).json() as { success: number; failed: number; errors: string[] };
      
      setImportResults(results);
      
      if (results.success > 0) {
        queryClient.invalidateQueries({ queryKey: [`/api/${entityType}`] });
        onImportComplete?.();
      }

      toast({
        title: "Update Complete",
        description: `Successfully updated ${results.success} records. ${results.failed} failed.`,
        variant: results.failed > 0 ? "destructive" : "default",
      });
    } catch (error: any) {
      toast({
        title: "Update Failed",
        description: error.message || "An error occurred during update.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const closeDialogs = () => {
    setIsImportDialogOpen(false);
    setIsUpdateDialogOpen(false);
    setIsDuplicateDialogOpen(false);
    setCsvData([]);
    setCsvHeaders([]);
    setFieldMapping({});
    setImportResults(null);
    setDuplicates([]);
    setUniqueRecords([]);
    setDuplicateResolutions({});
    setDefaultClientType("");
    setDefaultRegionId("");
    setDefaultOwnerId("");
    setRegionPickerOpen(false);
    setRegionQuery("");
    setCreatingRegion(false);
    setNewRegionName("");
  };

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        accept=".csv"
        className="hidden"
        onChange={handleFileChange}
        data-testid="input-csv-file"
      />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size={size} disabled={isProcessing} data-testid="button-bulk-actions">
            {isProcessing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                Bulk Actions
                <ChevronDown className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={exportToCsv} data-testid="dropdown-bulk-export">
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleFileSelect("import")} data-testid="dropdown-bulk-import">
            <Upload className="mr-2 h-4 w-4" />
            Import CSV
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleFileSelect("update")} data-testid="dropdown-bulk-update">
            <RefreshCw className="mr-2 h-4 w-4" />
            Update CSV
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Import Dialog */}
      <Dialog open={isImportDialogOpen} onOpenChange={(open) => !open && closeDialogs()}>
        <DialogContent className="w-full sm:max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import {entityType.charAt(0).toUpperCase() + entityType.slice(1)} from CSV</DialogTitle>
            <DialogDescription>
              Map CSV columns to fields. Required fields are marked with *.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="text-sm text-muted-foreground">
              Found {csvData.length} rows to import
            </div>

            {showLeadDefaults && (
              <div className="rounded-lg border p-4 bg-muted/30 space-y-3">
                <div className="text-sm font-medium">
                  Apply to all imported leads
                  <span className="text-muted-foreground font-normal ml-2">
                    (used as a fallback for any row missing the value)
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {/* Client Type */}
                  <div className="space-y-1.5">
                    <Label htmlFor="bulk-default-client-type">Client Type</Label>
                    <Select
                      value={defaultClientType || "__none__"}
                      onValueChange={(v) => setDefaultClientType(v === "__none__" ? "" : v)}
                    >
                      <SelectTrigger id="bulk-default-client-type" data-testid="select-bulk-default-client-type">
                        <SelectValue placeholder="Select client type…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">
                          <span className="text-muted-foreground">Don't apply — use CSV per row</span>
                        </SelectItem>
                        {clientTypes.map((ct) => (
                          <SelectItem key={ct.id} value={ct.name}>{ct.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Region (with create-new) */}
                  <div className="space-y-1.5">
                    <Label>Region</Label>
                    <Popover
                      modal={false}
                      open={regionPickerOpen}
                      onOpenChange={(open) => {
                        setRegionPickerOpen(open);
                        if (!open) setRegionQuery("");
                      }}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={regionPickerOpen}
                          className="w-full justify-between font-normal"
                          data-testid="select-bulk-default-region"
                        >
                          <span className={defaultRegionId ? "text-foreground" : "text-muted-foreground"}>
                            {defaultRegionId
                              ? regions.find((r) => r.id === defaultRegionId)?.name ?? "Select region…"
                              : "Select region…"}
                          </span>
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-full p-0"
                        align="start"
                        side="bottom"
                        sideOffset={4}
                        avoidCollisions
                        collisionPadding={16}
                        style={{ width: "var(--radix-popover-trigger-width)" }}
                      >
                        <Command>
                          <CommandInput
                            placeholder="Search regions…"
                            value={regionQuery}
                            onValueChange={setRegionQuery}
                          />
                          <CommandList className="max-h-[260px] overflow-y-auto overscroll-contain">
                            <CommandGroup>
                              <CommandItem
                                value="__create_new_region__"
                                onSelect={() => {
                                  setRegionPickerOpen(false);
                                  setNewRegionName(regionQuery.trim());
                                  setCreatingRegion(true);
                                }}
                                className="text-primary font-medium"
                                data-testid="option-bulk-region-create-new"
                              >
                                <span className="mr-2 text-base leading-none">+</span>
                                Create new region
                                {regionQuery.trim() ? ` "${regionQuery.trim()}"` : "…"}
                              </CommandItem>
                              <CommandItem
                                value="__none_use_csv_per_row__"
                                onSelect={() => {
                                  setDefaultRegionId("");
                                  setRegionPickerOpen(false);
                                  setRegionQuery("");
                                }}
                                data-testid="option-bulk-region-none"
                              >
                                <Check
                                  className={`mr-2 h-4 w-4 ${
                                    !defaultRegionId ? "opacity-100" : "opacity-0"
                                  }`}
                                />
                                <span className="text-muted-foreground">
                                  Don't apply — use CSV per row
                                </span>
                              </CommandItem>
                            </CommandGroup>
                            <CommandEmpty>
                              <div className="px-2 py-1.5 text-sm text-muted-foreground">
                                No matching region. Use "+ Create new region" above.
                              </div>
                            </CommandEmpty>
                            <CommandGroup heading="Regions">
                              {regions.map((r) => (
                                <CommandItem
                                  key={r.id}
                                  value={r.name}
                                  onSelect={() => {
                                    setDefaultRegionId(r.id);
                                    setRegionPickerOpen(false);
                                    setRegionQuery("");
                                  }}
                                  data-testid={`option-bulk-region-${r.id}`}
                                >
                                  <Check
                                    className={`mr-2 h-4 w-4 ${
                                      defaultRegionId === r.id ? "opacity-100" : "opacity-0"
                                    }`}
                                  />
                                  {r.name}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>

                    {creatingRegion && (
                      <div className="flex items-center gap-2 mt-1.5">
                        <Input
                          autoFocus
                          placeholder="Region name…"
                          value={newRegionName}
                          onChange={(e) => setNewRegionName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && newRegionName.trim())
                              createRegionMutation.mutate(newRegionName.trim());
                            if (e.key === "Escape") {
                              setCreatingRegion(false);
                              setNewRegionName("");
                            }
                          }}
                          className="h-8 text-sm"
                          data-testid="input-bulk-new-region-name"
                        />
                        <Button
                          size="sm"
                          className="h-8 shrink-0"
                          onClick={() =>
                            newRegionName.trim() && createRegionMutation.mutate(newRegionName.trim())
                          }
                          disabled={!newRegionName.trim() || createRegionMutation.isPending}
                          data-testid="button-bulk-create-region"
                        >
                          {createRegionMutation.isPending ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            "Create"
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 shrink-0"
                          onClick={() => {
                            setCreatingRegion(false);
                            setNewRegionName("");
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Owner (Rep) */}
                  <div className="space-y-1.5">
                    <Label htmlFor="bulk-default-owner">Owner / Rep</Label>
                    <Select
                      value={defaultOwnerId || "__none__"}
                      onValueChange={(v) => setDefaultOwnerId(v === "__none__" ? "" : v)}
                    >
                      <SelectTrigger id="bulk-default-owner" data-testid="select-bulk-default-owner">
                        <SelectValue placeholder="Select owner…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">
                          <span className="text-muted-foreground">Don't apply — use CSV per row</span>
                        </SelectItem>
                        {usersList.map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            {formatUser(u)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[200px]">CSV Column</TableHead>
                    <TableHead className="w-[200px]">Sample Value</TableHead>
                    <TableHead className="w-[200px]">Map to Field</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {csvHeaders.map((header, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{header}</TableCell>
                      <TableCell className="text-muted-foreground text-sm truncate max-w-[200px]">
                        {csvData[0]?.[index] || "—"}
                      </TableCell>
                      <TableCell>
                        <Select
                          value={fieldMapping[String(index)] || "__skip__"}
                          onValueChange={(value) => handleMappingChange(String(index), value)}
                        >
                          <SelectTrigger className="w-[180px]" data-testid={`select-mapping-${index}`}>
                            <SelectValue placeholder="Skip column" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__skip__">Skip column</SelectItem>
                            {fields.map(field => (
                              <SelectItem key={field.key} value={field.key}>
                                {field.label} {field.required ? "*" : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {importResults && (
              <div className={`p-4 rounded-md ${importResults.failed > 0 ? "bg-destructive/10" : "bg-green-500/10"}`}>
                <div className="flex items-center gap-2 mb-2">
                  {importResults.failed > 0 ? (
                    <AlertCircle className="h-5 w-5 text-destructive" />
                  ) : (
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  )}
                  <span className="font-medium">
                    {importResults.success} imported, {importResults.failed} failed
                    {importResults.skipped ? `, ${importResults.skipped} skipped` : ""}
                  </span>
                </div>
                {importResults.errors.length > 0 && (
                  <div className="mt-3">
                    <div className="font-medium text-sm mb-2 text-destructive">Failed Records:</div>
                    <div className="text-sm bg-background border rounded-md max-h-[200px] overflow-y-auto">
                      {(importResults.errorSummary || importResults.errors).slice(0, 50).map((error, i) => (
                        <div key={i} className="px-3 py-2 border-b last:border-b-0 text-muted-foreground">
                          {typeof error === "string" ? error : `Row ${error.row} (${error.accountName}): ${error.error}`}
                        </div>
                      ))}
                      {importResults.errors.length > 50 && (
                        <div className="px-3 py-2 text-muted-foreground italic">
                          ...and {importResults.errors.length - 50} more errors
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialogs} data-testid="button-cancel-import">
              {importResults ? "Close" : "Cancel"}
            </Button>
            {!importResults && (
              <Button onClick={handleImport} disabled={isProcessing} data-testid="button-confirm-import">
                {isProcessing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Import {csvData.length} Records
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Dialog */}
      <Dialog open={isUpdateDialogOpen} onOpenChange={(open) => !open && closeDialogs()}>
        <DialogContent className="w-full sm:max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Update {entityType.charAt(0).toUpperCase() + entityType.slice(1)} from CSV</DialogTitle>
            <DialogDescription>
              Map CSV columns to fields. You must map a column to &quot;{entityIdLabel}&quot; to identify which records to update.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="text-sm text-muted-foreground">
              Found {csvData.length} rows to update
            </div>

            {!Object.values(fieldMapping).includes("id") && (
              <div className="p-3 rounded-md bg-amber-500/10 text-amber-700 dark:text-amber-400 flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm">Map a column to &quot;{entityIdLabel}&quot; to enable updates</span>
              </div>
            )}

            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[200px]">CSV Column</TableHead>
                    <TableHead className="w-[200px]">Sample Value</TableHead>
                    <TableHead className="w-[200px]">Map to Field</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {csvHeaders.map((header, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{header}</TableCell>
                      <TableCell className="text-muted-foreground text-sm truncate max-w-[200px]">
                        {csvData[0]?.[index] || "—"}
                      </TableCell>
                      <TableCell>
                        <Select
                          value={fieldMapping[String(index)] || "__skip__"}
                          onValueChange={(value) => handleMappingChange(String(index), value)}
                        >
                          <SelectTrigger className="w-[180px]" data-testid={`select-update-mapping-${index}`}>
                            <SelectValue placeholder="Skip column" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__skip__">Skip column</SelectItem>
                            <SelectItem value="id">{entityIdLabel} (required for update)</SelectItem>
                            {fields.filter(f => f.key !== "id").map(field => (
                              <SelectItem key={field.key} value={field.key}>
                                {field.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {importResults && (
              <div className={`p-4 rounded-md ${importResults.failed > 0 ? "bg-destructive/10" : "bg-green-500/10"}`}>
                <div className="flex items-center gap-2 mb-2">
                  {importResults.failed > 0 ? (
                    <AlertCircle className="h-5 w-5 text-destructive" />
                  ) : (
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  )}
                  <span className="font-medium">
                    {importResults.success} updated, {importResults.failed} failed
                  </span>
                </div>
                {importResults.errors.length > 0 && (
                  <div className="mt-3">
                    <div className="font-medium text-sm mb-2 text-destructive">Failed Records:</div>
                    <div className="text-sm bg-background border rounded-md max-h-[200px] overflow-y-auto">
                      {(importResults.errorSummary || importResults.errors).slice(0, 50).map((error, i) => (
                        <div key={i} className="px-3 py-2 border-b last:border-b-0 text-muted-foreground">
                          {typeof error === "string" ? error : `Row ${error.row} (${error.accountName}): ${error.error}`}
                        </div>
                      ))}
                      {importResults.errors.length > 50 && (
                        <div className="px-3 py-2 text-muted-foreground italic">
                          ...and {importResults.errors.length - 50} more errors
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialogs} data-testid="button-cancel-update">
              {importResults ? "Close" : "Cancel"}
            </Button>
            {!importResults && (
              <Button 
                onClick={handleUpdate} 
                disabled={isProcessing || !Object.values(fieldMapping).includes("id")} 
                data-testid="button-confirm-update"
              >
                {isProcessing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Update {csvData.length} Records
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Resolution Dialog */}
      <Dialog open={isDuplicateDialogOpen} onOpenChange={(open) => !open && closeDialogs()}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              Duplicates Detected
            </DialogTitle>
            <DialogDescription>
              We found {duplicates.length} {entityType === "leads" ? "lead" : "client"} name(s) that already exist in your system. 
              Choose how to handle each duplicate below.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="text-sm text-muted-foreground">
              {uniqueRecords.length} unique records will be imported. 
              {duplicates.length} records need your decision.
            </div>

            <div className="rounded-md border overflow-x-auto max-h-[400px] overflow-y-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-background z-10">
                  <TableRow>
                    <TableHead className="w-[40px]">#</TableHead>
                    <TableHead className="w-[180px]">{entityType === "leads" ? "Lead Name" : "Client Name"} (Import)</TableHead>
                    <TableHead className="w-[180px]">Existing Match</TableHead>
                    {entityType === "leads" && <TableHead className="w-[120px]">Matched On</TableHead>}
                    <TableHead className="w-[210px]">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {duplicates.map((dup, index) => (
                    <TableRow key={dup.rowIndex}>
                      <TableCell className="text-muted-foreground">{index + 1}</TableCell>
                      <TableCell>
                        <div className="font-medium">{dup.importName}</div>
                        {dup.importData.city && (
                          <div className="text-xs text-muted-foreground">
                            {dup.importData.city}{dup.importData.state ? `, ${dup.importData.state}` : ""}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {dup.existingClients.map((client, i) => (
                          <div key={client.id} className={i > 0 ? "mt-1 pt-1 border-t" : ""}>
                            <div className="text-sm">{client.accountName}</div>
                            {(client.city || client.state) && (
                              <div className="text-xs text-muted-foreground">
                                {client.city}{client.state ? `, ${client.state}` : ""}
                              </div>
                            )}
                          </div>
                        ))}
                      </TableCell>
                      {entityType === "leads" && (
                        <TableCell>
                          {dup.existingClients[0]?.matchedFields?.map((f, fi) => (
                            <span key={fi} className="inline-block text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded px-1 py-0.5 mr-1 mb-1">{f}</span>
                          ))}
                        </TableCell>
                      )}
                      <TableCell>
                        <Select
                          value={
                            duplicateResolutions[dup.rowIndex]?.action === "link" && duplicateResolutions[dup.rowIndex]?.linkToClientId
                              ? `link:${duplicateResolutions[dup.rowIndex].linkToClientId}`
                              : duplicateResolutions[dup.rowIndex]?.action === "merge" && duplicateResolutions[dup.rowIndex]?.mergeToLeadId
                              ? `merge:${duplicateResolutions[dup.rowIndex].mergeToLeadId}`
                              : duplicateResolutions[dup.rowIndex]?.action || "skip"
                          }
                          onValueChange={(value) => {
                            if (value.startsWith("link:")) {
                              const clientId = value.replace("link:", "");
                              handleDuplicateResolution(dup.rowIndex, "link", clientId, undefined);
                            } else if (value.startsWith("merge:")) {
                              const leadId = value.replace("merge:", "");
                              handleDuplicateResolution(dup.rowIndex, "merge", undefined, leadId);
                            } else {
                              handleDuplicateResolution(dup.rowIndex, value as "create" | "skip", undefined, undefined);
                            }
                          }}
                        >
                          <SelectTrigger className="w-[200px]" data-testid={`select-duplicate-action-${index}`}>
                            <SelectValue placeholder="Choose action" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="skip">Skip this record</SelectItem>
                            <SelectItem value="create">Import anyway (create new)</SelectItem>
                            {entityType === "clients" && dup.existingClients.map(client => (
                              <SelectItem key={client.id} value={`link:${client.id}`}>
                                Link to: {client.accountName.substring(0, 20)}{client.accountName.length > 20 ? "..." : ""}
                              </SelectItem>
                            ))}
                            {entityType === "leads" && dup.existingClients.map(client => (
                              <SelectItem key={client.id} value={`merge:${client.id}`}>
                                Merge into: {client.accountName.substring(0, 20)}{client.accountName.length > 20 ? "..." : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="flex flex-wrap gap-3 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span>{uniqueRecords.length} will be imported</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                <span>{Object.values(duplicateResolutions).filter(r => r.action === "create").length} will create new</span>
              </div>
              {entityType === "leads" && (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                  <span>{Object.values(duplicateResolutions).filter(r => r.action === "merge").length} will merge</span>
                </div>
              )}
              {entityType === "clients" && (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span>{Object.values(duplicateResolutions).filter(r => r.action === "link").length} will link</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-gray-400"></div>
                <span>{Object.values(duplicateResolutions).filter(r => r.action === "skip").length} will skip</span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialogs} data-testid="button-cancel-duplicates">
              Cancel
            </Button>
            <Button onClick={handleConfirmDuplicateResolution} disabled={isProcessing} data-testid="button-confirm-duplicates">
              {isProcessing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Continue Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
